import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Helmet } from "react-helmet-async";
import { getMe, startAuth, verifyAuth, logoutAuth, resubscribeAuth } from "@/lib/api";
import { inputCls, labelCls } from "@/lib/modal";
import {
  Loader2, Copy, Check, ShieldCheck, CheckCircle2, Lock, LogOut, BellRing,
} from "lucide-react";
import { toast } from "sonner";

const EASE = [0.22, 1, 0.36, 1];

const MILESTONES = [
  { at: 25, label: "NETWORK BUILDER" },
  { at: 10, label: "CONNECTOR" },
  { at: 5, label: "SPARK" },
];

const nextMilestone = (n) =>
  [...MILESTONES].sort((a, b) => a.at - b.at).find((m) => n < m.at) || null;

const JOURNEY = [
  { label: "Join the network", state: "done" },
  { label: "Verify your email", state: "done" },
  { label: "Build your network", state: "active" },
  { label: "Pilot access", state: "soon" },
  { label: "Contribute & earn", state: "soon" },
];

export default function Portal() {
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState(searchParams.get("email") || "");
  const [otp, setOtp] = useState("");
  const [stage, setStage] = useState("form");
  const [loading, setLoading] = useState(false);
  const [checking, setChecking] = useState(true);
  const [profile, setProfile] = useState(null);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    getMe()
      .then(setProfile)
      .catch(() => {})
      .finally(() => setChecking(false));
  }, []);

  // Refresh the profile whenever the member returns to the tab, so newly
  // verified invites show up without a manual reload. pageshow covers
  // back/forward-cache restores (e.g. pressing Back right after logout).
  useEffect(() => {
    const refresh = () => {
      getMe()
        .then(setProfile)
        .catch((e) => {
          if (e.response?.status === 401) setProfile(null);
        });
    };
    const onVisible = () => {
      if (document.visibilityState === "visible") refresh();
    };
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", refresh);
    window.addEventListener("pageshow", refresh);
    return () => {
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", refresh);
      window.removeEventListener("pageshow", refresh);
    };
  }, []);

  const errText = (err, fallback) => {
    const msg = err.response?.data?.detail;
    return typeof msg === "string" ? msg : fallback;
  };

  const submitEmail = async (e) => {
    e.preventDefault();
    if (loading) return;
    setLoading(true);
    setError("");
    try {
      await startAuth({ email: email.trim() });
      setStage("otp");
    } catch (err) {
      setError(errText(err, "Could not send your sign-in code. Please try again."));
    } finally {
      setLoading(false);
    }
  };

  const submitOtp = async (e) => {
    e.preventDefault();
    if (loading) return;
    setLoading(true);
    setError("");
    try {
      const data = await verifyAuth({ email: email.trim(), otp: otp.trim() });
      setProfile(data.member);
    } catch (err) {
      setError(errText(err, "Verification failed. Please try again."));
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    try {
      await logoutAuth();
    } catch {}
    setProfile(null);
    setStage("form");
    setOtp("");
    toast.success("Signed out.");
  };

  const resubscribe = async () => {
    try {
      await resubscribeAuth();
      setProfile((p) => ({ ...p, unsubscribed: false }));
      toast.success("You're back on the Founding updates list.");
    } catch {
      toast.error("Could not update your preference. Please try again.");
    }
  };

  const shareUrl = profile ? `${window.location.origin}/invite/${profile.genesis_id}` : "";
  const shareText = encodeURIComponent(
    `Join me on the Synaphy contributor waitlist · the Physical World Intelligence Network. Founding Phase is open: ${shareUrl}`
  );

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      toast.error("Copy failed");
    }
  };

  const initials = profile
    ? profile.name.split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase()
    : "";

  return (
    <div className="relative min-h-screen bg-ink-950" data-testid="portal-page">
      <Helmet>
        <title>Founding Portal - Synaphy</title>
        <meta name="description" content="Access your Synaphy Founding contributor profile, position and invite engine." />
        <meta name="robots" content="noindex" />
      </Helmet>
      <div className="absolute left-1/2 top-0 -translate-x-1/2 h-[380px] w-[680px] rounded-full bg-signal-cyan/[0.05] blur-[140px] pointer-events-none" />

      <header className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8 h-[72px] flex items-center justify-between border-b border-white/5">
        <Link to="/" className="flex items-center gap-3 group" data-testid="portal-logo" aria-label="Synaphy home">
          <img src="/logo-lockup.png" alt="Synaphy - Intelligence for the physical world" className="h-14 w-auto object-contain drop-shadow-[0_0_18px_rgba(90,150,255,0.35)] transition-transform duration-300 group-hover:scale-105" />
        </Link>
        <div className="flex items-center gap-5">
          <span className="text-sm text-steel">Founding Portal</span>
          {profile && (
            <button
              onClick={logout}
              data-testid="portal-logout-btn"
              aria-label="Log out"
              title="Log out"
              className="group inline-flex h-9 w-9 items-center justify-center rounded-full border border-white/10 bg-white/[0.03] text-steel backdrop-blur-sm transition-[color,border-color,background-color,box-shadow] duration-300 hover:border-signal-coral/50 hover:bg-signal-coral/[0.07] hover:text-signal-coral hover:shadow-[0_0_24px_rgba(255,107,97,0.18)]"
            >
              <LogOut className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5" />
            </button>
          )}
        </div>
      </header>

      <main className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8 pt-14 sm:pt-20 pb-24">
        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, ease: EASE }}>
          <h1 className="font-display text-4xl sm:text-6xl font-bold tracking-tight text-mist" data-testid="portal-title">
            Your Founding position.
          </h1>
          {!profile && !checking && (
            <p className="mt-5 max-w-md text-lg leading-relaxed text-steel">
              Enter the email you registered with and we will send you a secure
              sign-in code.
            </p>
          )}
        </motion.div>

        {checking && !profile ? (
          <div className="mt-16 flex justify-start">
            <Loader2 className="h-6 w-6 animate-spin text-signal-cyan" />
          </div>
        ) : !profile ? (
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.15, ease: EASE }}
            className="mt-14 max-w-md"
          >
            {stage === "form" ? (
              <form onSubmit={submitEmail} className="space-y-6" data-testid="portal-form">
                <div>
                  <label htmlFor="portal-email" className={labelCls}>Email</label>
                  <input
                    id="portal-email"
                    data-testid="portal-email-input"
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@example.com"
                    className={inputCls}
                  />
                </div>
                <button
                  type="submit"
                  data-testid="portal-access-btn"
                  disabled={loading}
                  className="inline-flex items-center justify-center gap-2 rounded-lg bg-signal-cyan px-7 py-3.5 text-sm font-semibold text-ink-950 transition-[background-color,box-shadow] duration-300 hover:bg-cyan-300 hover:shadow-[0_8px_40px_rgba(34,211,238,0.25)] disabled:opacity-60"
                >
                  {loading && <Loader2 className="h-4 w-4 animate-spin" />}
                  Email me a sign-in code
                </button>
              </form>
            ) : (
              <form onSubmit={submitOtp} className="space-y-6" data-testid="portal-otp-form">
                <div>
                  <label htmlFor="portal-otp" className={labelCls}>
                    Verification code sent to {email}
                  </label>
                  <input
                    id="portal-otp"
                    data-testid="portal-otp-input"
                    required
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    maxLength={6}
                    value={otp}
                    onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
                    placeholder="······"
                    className={`${inputCls} font-mono text-xl tracking-[0.5em]`}
                  />
                </div>
                <button
                  type="submit"
                  data-testid="portal-verify-btn"
                  disabled={loading || otp.length !== 6}
                  className="inline-flex items-center justify-center gap-2 rounded-lg bg-signal-cyan px-7 py-3.5 text-sm font-semibold text-ink-950 transition-[background-color,box-shadow] duration-300 hover:bg-cyan-300 hover:shadow-[0_8px_40px_rgba(34,211,238,0.25)] disabled:opacity-60"
                >
                  {loading && <Loader2 className="h-4 w-4 animate-spin" />}
                  Verify &amp; open portal
                </button>
                <button
                  type="button"
                  onClick={() => { setStage("form"); setOtp(""); setError(""); }}
                  className="block text-sm text-steel transition-colors hover:text-mist"
                  data-testid="portal-back-btn"
                >
                  Use a different email
                </button>
              </form>
            )}
            {error && (
              <div
                data-testid="portal-error"
                role="alert"
                className="mt-6 border-l-2 border-signal-coral pl-4 py-1 text-sm leading-relaxed text-signal-coral"
              >
                {error}
              </div>
            )}
            <p className="mt-6 text-sm text-steel">
              Not on the waitlist yet?{" "}
              <Link to="/" className="text-signal-cyan hover:underline">Join free on the homepage</Link>
            </p>
          </motion.div>
        ) : (
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: EASE }}
            className="mt-14 grid grid-cols-1 lg:grid-cols-12 gap-16"
            data-testid="portal-dashboard"
          >
            <div className="lg:col-span-8">
              <div className="flex flex-wrap items-center gap-5 pb-8 border-b border-white/5">
                <span className="flex h-14 w-14 items-center justify-center rounded-full bg-signal-cyan/[0.1] border border-signal-cyan/25 font-display text-lg font-semibold text-signal-cyan">
                  {initials}
                </span>
                <div className="flex-1 min-w-[200px]">
                  <div className="font-display text-xl font-semibold tracking-tight text-mist" data-testid="portal-member-name">{profile.name}</div>
                  <div className="mt-1 font-mono text-sm tracking-[0.1em] text-signal-cyan" data-testid="portal-genesis-id">
                    {profile.genesis_id}
                  </div>
                  {profile.mobile && (
                    <div className="mt-1 text-sm text-steel" data-testid="portal-member-mobile">
                      {profile.mobile}
                    </div>
                  )}
                </div>
                <span className="flex items-center gap-2 text-sm text-steel">
                  <span className="relative flex h-1.5 w-1.5">
                    <span className="absolute inline-flex h-full w-full rounded-full bg-signal-green opacity-60 soft-ping" />
                    <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-signal-green" />
                  </span>
                  Verified contributor
                </span>
              </div>

              {profile.unsubscribed && (
                <div
                  className="mt-8 flex flex-wrap items-center justify-between gap-4 rounded-xl border border-signal-coral/25 bg-signal-coral/[0.05] px-5 py-4"
                  data-testid="portal-unsubscribed-banner"
                >
                  <p className="text-sm leading-relaxed text-steel max-w-sm">
                    You are opted out of Founding update emails. Changed your mind?
                  </p>
                  <button
                    onClick={resubscribe}
                    data-testid="portal-resubscribe-btn"
                    className="inline-flex items-center gap-2 rounded-lg border border-signal-cyan/40 bg-signal-cyan/[0.08] px-4 py-2 text-sm font-medium text-signal-cyan transition-[background-color,border-color] duration-300 hover:bg-signal-cyan/[0.15] hover:border-signal-cyan/60"
                  >
                    <BellRing className="h-4 w-4" />
                    Resume updates
                  </button>
                </div>
              )}

              <div className="grid grid-cols-2 sm:grid-cols-4 gap-8 py-10 border-b border-white/5">
                <div data-testid="portal-position-card">
                  <div className="font-display text-3xl sm:text-4xl font-bold tracking-tight text-mist tabular-nums" data-testid="portal-position-value">
                    #{String(profile.position).padStart(4, "0")}
                  </div>
                  <div className="mt-2 text-sm text-steel">Founding position</div>
                </div>
                <div data-testid="portal-referrals-card">
                  <div className="font-display text-3xl sm:text-4xl font-bold tracking-tight text-mist tabular-nums" data-testid="portal-referral-count">
                    {profile.referral_verified ?? profile.referral_count ?? 0}
                  </div>
                  <div className="mt-2 text-sm text-steel">Verified invites</div>
                  {(profile.referral_count ?? 0) > (profile.referral_verified ?? 0) && (
                    <div className="mt-1 text-xs text-zinc-500" data-testid="portal-referral-review-hint">
                      {profile.referral_count - (profile.referral_verified ?? 0)} under anti-fraud review
                    </div>
                  )}
                </div>
                <div data-testid="portal-since-card">
                  <div className="font-display text-xl sm:text-2xl font-semibold tracking-tight text-mist" data-testid="portal-member-since">
                    {new Date(profile.created_at).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" })}
                  </div>
                  <div className="mt-2 text-sm text-steel">Member since</div>
                </div>
                <div data-testid="portal-network-status-card">
                  <div className="font-display text-xl sm:text-2xl font-semibold tracking-tight text-signal-green" data-testid="portal-network-status">
                    Founding
                  </div>
                  <div className="mt-2 text-sm text-steel">Network status</div>
                </div>
              </div>

              <div className="py-10 border-b border-white/5" data-testid="portal-share-card">
                <h2 className="font-display text-xl font-semibold tracking-tight text-mist">
                  Grow your network
                </h2>
                <p className="mt-2 text-[15px] text-steel max-w-md">
                  Share your personal link. Invites count only after your friend
                  verifies their email and joins Founding.
                </p>

                <div className="mt-6 flex items-center gap-3 border-b border-white/10 pb-3" data-testid="invite-steps-mini">
                  <span className="flex-1 truncate font-mono text-[13px] tracking-[0.04em] text-signal-cyan" data-testid="portal-referral-link">
                    {shareUrl}
                  </span>
                  <button
                    data-testid="portal-copy-referral"
                    onClick={copy}
                    className="shrink-0 inline-flex items-center gap-1.5 text-sm font-medium text-steel transition-colors hover:text-signal-cyan"
                  >
                    {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                    {copied ? "Copied" : "Copy"}
                  </button>
                </div>

                <div className="mt-5 flex flex-wrap gap-x-8 gap-y-2">
                  <a data-testid="portal-share-whatsapp" href={`https://wa.me/?text=${shareText}`} target="_blank" rel="noopener noreferrer" className="text-sm font-medium text-steel transition-colors hover:text-signal-cyan">WhatsApp</a>
                  <a data-testid="portal-share-telegram" href={`https://t.me/share/url?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent("Join me on the Synaphy contributor waitlist.")}`} target="_blank" rel="noopener noreferrer" className="text-sm font-medium text-steel transition-colors hover:text-signal-cyan">Telegram</a>
                  <a data-testid="portal-share-x" href={`https://twitter.com/intent/tweet?text=${shareText}`} target="_blank" rel="noopener noreferrer" className="text-sm font-medium text-steel transition-colors hover:text-signal-cyan">X</a>
                </div>

                {(() => {
                  const rc = profile.referral_verified ?? profile.referral_count ?? 0;
                  const pct = Math.min(100, (rc / 25) * 100);
                  const next = nextMilestone(rc);
                  return (
                    <div className="mt-10" data-testid="portal-milestone-progress">
                      <div className="flex items-center justify-between mb-3 text-sm">
                        <span className="text-steel">{rc} verified invites</span>
                        {next ? (
                          <span className="text-signal-cyan" data-testid="portal-milestone-hint">
                            {next.at - rc} more to {next.label.toLowerCase()}
                          </span>
                        ) : (
                          <span className="text-signal-green" data-testid="portal-milestone-hint">
                            Highest milestone reached
                          </span>
                        )}
                      </div>
                      <div className="h-1 rounded-full bg-white/[0.07]">
                        <div
                          className="h-full rounded-full bg-gradient-to-r from-signal-cyan to-signal-green transition-all duration-700"
                          style={{ width: `${pct}%` }}
                        />
                      </div>
                      <div className="mt-3 flex justify-between font-mono text-[9px] tracking-[0.15em] text-zinc-600 uppercase">
                        <span className={rc >= 5 ? "text-signal-violet" : ""}>Spark · 5</span>
                        <span className={rc >= 10 ? "text-signal-cyan" : ""}>Connector · 10</span>
                        <span className={rc >= 25 ? "text-signal-green" : ""}>Builder · 25</span>
                      </div>
                    </div>
                  );
                })()}

                <p className="mt-8 flex items-start gap-2.5 text-sm leading-relaxed text-steel/80" data-testid="portal-verification-note">
                  <ShieldCheck className="h-4 w-4 text-signal-green shrink-0 mt-0.5" />
                  Referral count = verified Founding contributors only. Link
                  opens and unverified attempts never count.
                </p>

                {(profile.invites || []).length > 0 && (
                  <div className="mt-10" data-testid="portal-invites-list">
                    <h3 className="font-display text-base font-semibold tracking-tight text-mist">
                      Your invites
                    </h3>
                    <div className="mt-4">
                      {profile.invites.map((inv, i) => (
                        <div
                          key={`${inv.name}-${i}`}
                          className="flex items-center justify-between py-3 border-t border-white/5"
                          data-testid={`portal-invite-row-${i}`}
                        >
                          <span className="flex items-center gap-3">
                            <span className="flex h-7 w-7 items-center justify-center rounded-full bg-signal-green/[0.1] border border-signal-green/25 text-[11px] font-semibold text-signal-green">
                              {inv.name.split(" ").map((w) => w[0]).slice(0, 2).join("").replace(".", "")}
                            </span>
                            <span className="text-sm font-medium text-mist">{inv.name}</span>
                          </span>
                          <span className="flex items-center gap-3">
                            <span
                              data-testid={`portal-invite-status-${i}`}
                              className={`rounded-full border px-2 py-0.5 font-mono text-[9px] uppercase tracking-[0.15em] ${
                                inv.status === "verified"
                                  ? "border-signal-green/25 text-signal-green bg-signal-green/[0.08]"
                                  : "border-amber-400/30 text-amber-400 bg-amber-400/[0.08]"
                              }`}
                            >
                              {inv.status === "verified" ? "Verified" : "In review"}
                            </span>
                            <span className="text-xs text-zinc-500 tabular-nums">
                              {new Date(inv.joined_at).toLocaleDateString(undefined, { month: "short", day: "numeric" })}
                            </span>
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="lg:col-span-4">
              <div data-testid="portal-journey"><h2 className="font-display text-xl font-semibold tracking-tight text-mist">
                  Your Founding journey
                </h2>
                <div className="mt-7">
                  {JOURNEY.map((j, i) => (
                    <div key={j.label} className="flex items-start gap-4" data-testid={`journey-step-${i}`}>
                      <div className="flex flex-col items-center">
                        {j.state === "done" ? (
                          <CheckCircle2 className="h-5 w-5 text-signal-green shrink-0" />
                        ) : j.state === "active" ? (
                          <span className="relative flex h-5 w-5 items-center justify-center">
                            <span className="absolute inline-flex h-3 w-3 rounded-full bg-signal-cyan opacity-40 soft-ping" />
                            <span className="relative inline-flex h-2 w-2 rounded-full bg-signal-cyan" />
                          </span>
                        ) : (
                          <Lock className="h-4 w-4 text-zinc-600 shrink-0 mt-0.5" />
                        )}
                        {i < JOURNEY.length - 1 && (
                          <span className={`w-px h-8 mt-1 ${j.state === "done" ? "bg-signal-green/30" : "bg-white/[0.07]"}`} />
                        )}
                      </div>
                      <div className="pb-6">
                        <div className={`text-[15px] font-medium ${j.state === "soon" ? "text-zinc-600" : j.state === "active" ? "text-signal-cyan" : "text-mist"}`}>
                          {j.label}
                        </div>
                        {j.state !== "done" && (
                          <div className="mt-0.5 text-xs text-zinc-600">
                            {j.state === "active" ? "In progress" : "Coming soon"}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
                <p className="pt-6 border-t border-white/5 text-sm text-steel/80 leading-relaxed">
                  Pilot access and missions unlock with the full Founding
                  Portal release.
                </p>
              </div>
            </div>
          </motion.div>
        )}
      </main>
    </div>
  );
}
